<template>
  <custom-callout
    border-color="primary-2"
    class="mb-4 custom-callout rounded"
    :has-toggle="false"
  >
    <div class="py-2 pr-3 ml-2 ml-md-n2">
      <div class="d-flex">
        <icon-view
          class="mb-3"
          :gradient="false"
          icon="fas fa-file-invoice mb-1"
          icon-background="white"
          icon-color="#FF7058"
          icon-size="16px"
          :title="title"
        />
      </div>
      <validation-observer ref="addTeacherObserver">
        <div class="row my-3 ml-4">
          <div class="col-md-3 col-sm-12 mr-md mb-2">
            <div v-if="showManualSSNInput">
              <validation-provider
                v-slot="{ errors }"
                :rules="ssnRules"
                :custom-messages="{
                  required: 'El número de Seguro Social es requerido.',
                  completePersonalssn:
                    'El número de Seguro Social debe ser correcto.',
                }"
              >
                <custom-input
                  id="txtTeacherSSN"
                  v-model="model.socialSecurityNo"
                  label="Número de Seguro Social:"
                  mask-type="Personalssn"
                  maxlength="11"
                  placeholder="xxx-xx-xxxx"
                  :error="ssnFieldError(errors)"
                  :custom-error-msg="ssnFieldError(errors)"
                  @input="onSSNInput"
                />
              </validation-provider>
            </div>

            <div v-if="showGeneratedSSNLabel" class="generated-ssn-wrapper">
              <label class="input-label">Número de Seguro Social:</label>
              <div class="generated-ssn-value">{{ maskedGeneratedSSN }}</div>
            </div>

            <div class="custom-control custom-checkbox mt-2">
              <input
                id="no-social-security-checkbox"
                v-model="noSocialSecuritySelection"
                type="checkbox"
                class="custom-control-input"
                @change="onNoSocialSecurityOptionChange"
              />
              <label
                class="custom-control-label custom-radio-input-label"
                for="no-social-security-checkbox"
              >
                No cuento con seguro social
              </label>
            </div>
          </div>

          <div class="col-md-3 col-sm-12 mr-md mb-2 mt-3 mt-md-0">
            <validation-provider
              v-slot="{ errors }"
              :rules="'required'"
              :custom-messages="{
                required: 'El número de puesto es requerido.',
              }"
            >
              <custom-input
                id="txtTeacherPositionNumber"
                v-model="model.positionNumber"
                label="Número de puesto:"
                placeholder="Escriba el número de puesto"
                :error="positionNumberFieldError(errors)"
                :custom-error-msg="positionNumberFieldError(errors)"
                @input="onPositionNumberInput"
              />
            </validation-provider>
          </div>

          <div class="col-md-3 col-sm-12 mr-md mb-2 mt-3 mt-md-0">
            <validation-provider
              v-slot="{ errors }"
              :rules="'required'"
              :custom-messages="{
                required: 'El nombre del maestro es requerido.',
              }"
            >
              <custom-input
                id="txtTeacherName"
                v-model="model.name"
                label="Nombre del Maestro:"
                placeholder="Escriba el nombre"
                :error="errors[0]"
                :custom-error-msg="errors[0]"
              />
            </validation-provider>
          </div>
        </div>
        <div class="row ml-4 mt-2">
          <div
            class="col-12 d-flex justify-content-md-end justify-content-start"
          >
            <action-button
              i="fas fa-times-circle"
              text="Cancelar"
              variant="outline-danger mr-3 pl-3 pr-4"
              @click="onClose"
            />
            <action-button
              i="fas fa-save"
              text="Guardar"
              variant="success pl-3 pr-3"
              @click="onSave"
            />
          </div>
        </div>
      </validation-observer>
    </div>
  </custom-callout>
</template>

<script>
// third party components
import CustomCallout from 'custom-callout';
import ActionButton from 'ActionButton';
import IconView from 'icon-view';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

// custom components
import CustomInput from '@/components/common/CustomInput';

// mixins
import alertMixin from '@/mixin/alert.mixin';

// services
import {
  addTeacher,
  generateRandomSSN,
} from '@/modules/alternative-location-management/api';

export default {
  name: 'AddTeacherSection',
  components: {
    CustomCallout,
    IconView,
    ActionButton,
    CustomInput,
    ValidationObserver,
    ValidationProvider,
  },
  mixins: [alertMixin],
  props: {
    title: {
      type: String,
      required: true,
    },
    schoolAlternativeLocationId: {
      type: [Number, String],
      required: true,
    },
  },
  data: () => ({
    model: {
      name: '',
      socialSecurityNo: '',
      positionNumber: '',
    },
    noSocialSecuritySelection: false,
    ssnErrorMessage: '',
    positionNumberErrorMessage: '',
  }),
  computed: {
    showManualSSNInput() {
      return this.noSocialSecuritySelection === false;
    },
    showGeneratedSSNLabel() {
      return this.noSocialSecuritySelection === true;
    },
    maskedGeneratedSSN() {
      const normalizedSSN = this.normalizeSSN(this.model.socialSecurityNo);
      const last4 = normalizedSSN.slice(-4).padStart(4, '#');
      return `**-***-${last4}`;
    },
    ssnRules() {
      if (!this.showManualSSNInput) return '';
      return {
        required: true,
        completePersonalssn: true,
      };
    },
  },
  methods: {
    ssnFieldError(errors) {
      return errors[0] || this.ssnErrorMessage;
    },
    positionNumberFieldError(errors) {
      return errors[0] || this.positionNumberErrorMessage;
    },
    onPositionNumberInput() {
      this.positionNumberErrorMessage = '';
    },
    onClose() {
      this.resetForm();
      this.$emit('on-close');
    },
    normalizeSSN(value) {
      return String(value || '').replace(/\D/g, '');
    },
    async generateUniqueRandomSSN() {
      const { data } = await generateRandomSSN();
      return data;
    },
    async onNoSocialSecurityOptionChange() {
      this.ssnErrorMessage = '';

      if (this.noSocialSecuritySelection) {
        await this.assignGeneratedSSN();
        return;
      }

      this.model.socialSecurityNo = '';
    },
    async assignGeneratedSSN() {
      try {
        this.model.socialSecurityNo = await this.generateUniqueRandomSSN();
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.title ||
          e?.response?.data?.error ||
          'No se pudo generar el número de Seguro Social.';
        this.ssnErrorMessage = msg;
        return false;
      }
    },
    onSSNInput() {
      this.ssnErrorMessage = '';
    },
    buildSavePayload() {
      const ssnDigits = this.normalizeSSN(this.model.socialSecurityNo);
      return {
        SchoolAlternativeLocationId: Number(this.schoolAlternativeLocationId),
        Name: (this.model.name || '').trim(),
        SocialSecurityNo: ssnDigits || null,
        PositionNumber: (this.model.positionNumber || '').trim(),
      };
    },
    async onSave() {
      const isValid = await this.$refs.addTeacherObserver.validate();
      if (!isValid) return;

      if (this.showGeneratedSSNLabel && !this.model.socialSecurityNo) {
        const isGenerated = await this.assignGeneratedSSN();
        if (!isGenerated) return;
      }

      const payload = this.buildSavePayload();

      try {
        const teacher = await addTeacher(payload);
        this.ShowToast(
          '',
          'El maestro ha sido guardado exitosamente.',
          'success'
        );
        this.$emit('on-save', teacher);
        this.resetForm();
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.title ||
          e?.response?.data?.error ||
          'No se pudo guardar el maestro.';
        this.ShowToast('Error', msg, 'error');
      }
    },
    resetForm() {
      this.model.name = '';
      this.model.socialSecurityNo = '';
      this.model.positionNumber = '';
      this.noSocialSecuritySelection = false;
      this.ssnErrorMessage = '';
      this.positionNumberErrorMessage = '';
      this.$refs.addTeacherObserver?.reset();
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables/_colors.scss';

.generated-ssn-wrapper {
  display: flex;
  flex-direction: column;
}

.generated-ssn-value {
  height: 38px;
  display: flex;
  align-items: center;
  border: 1px solid #c5cdcf;
  border-radius: 0.25rem;
  padding: 0 0.75rem;
  background-color: #f8f9fa;
}

.custom-radio-input-label {
  height: 19px;
  text-align: left;
  font: Bold 16px / 21px Lato;
  letter-spacing: 0;
  color: gray;
  line-height: 19px;
}
</style>
